import Settings from "@mui/icons-material/Settings";
import { IconButton, Popover, Tooltip } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback, useState } from "react";

import type { ScoutWriteModeGql } from "./hooks/scoutWriteMode";

import { ScoutWriteAccessToggle } from "./Chat/ScoutWriteAccessToggle";

export type ScoutConversationSettingsMenuProps = {
  writeMode: ScoutWriteModeGql;
  onWriteModeChange: (next: ScoutWriteModeGql) => void;
  disabled?: boolean;
  iconButtonSize?: "small" | "medium" | "large";
  hasScoutProAccess?: boolean;
};

export function ScoutConversationSettingsMenu({
  writeMode,
  onWriteModeChange,
  disabled = false,
  iconButtonSize = "medium",
  hasScoutProAccess = true,
}: ScoutConversationSettingsMenuProps) {
  const { featureFlags } = useFeatureFlags();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (!hasScoutProAccess || !featureFlags?.[FeatureFlagKey.ScoutWriteAccess]) {
    return null;
  }

  return (
    <>
      <Tooltip title="Conversation settings">
        <IconButton
          size={iconButtonSize}
          onClick={handleOpen}
          aria-label="Conversation settings"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Settings fontSize={iconButtonSize} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { mt: 0.5, minWidth: 320 },
          },
        }}
      >
        <ScoutWriteAccessToggle
          variant="menu"
          value={writeMode}
          disabled={disabled}
          onChange={onWriteModeChange}
        />
      </Popover>
    </>
  );
}
